/**
 * PHARMAKON — La CONSOLE d'alertes équipe (rédaction + tableau d'accusés).
 *
 * PLAN_mobile_pharma_owner_manager §3.6 et §4.2.
 *
 * DEUX VUES dans un seul tiroir :
 *   • « Nouvelle alerte » — brouillon → aperçu de l'audience → envoi ;
 *   • « Historique » — la boîte d'envoi, et pour chaque envoi le TABLEAU
 *     D'ACCUSÉS (§3.6 : Accusé / Vu, non accusé / Non vu), l'export CSV, le
 *     coupe-circuit « Annuler » et la réémission (L-1).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ÉCART DE PLACEMENT, assumé. Le plan situe le tableau à
 * `pages/equipe/AlerteRoster.jsx`, c'est-à-dire une ROUTE. Il est ici, dans un
 * tiroir monté par `AlerteHost`, pour deux raisons :
 *
 *  1. Ajouter une route veut dire toucher `App.tsx` ET `config/navigation.config.js`,
 *     deux fichiers partagés que d'autres lots de ce cycle modifient en même
 *     temps. Un composant neuf dans `components/alertes/**` n'entre en conflit
 *     avec personne.
 *  2. Le geste est TRANSVERSAL, pas destinationnel : on envoie une alerte
 *     depuis là où on est (le comptoir, un inventaire), pas en naviguant vers
 *     une page. Un raccourci clavier partout — Ctrl + Maj + A, l'idiome
 *     « clavier d'abord » de la maison — sert mieux ce geste qu'une entrée de
 *     menu de plus.
 *
 * Le jour où une entrée de menu est voulue, `AlerteConsole` se monte tel quel
 * dans une page : il ne suppose rien de son conteneur.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * CONFIDENTIALITÉ (§5.6 / Q6) : `peutRediger` et `peutVoirAccuses` excluent
 * délibérément `ADMIN_PHARMAKON`. Le support plateforme ne voit ni le contenu
 * ni le tableau — le serveur le refuse aussi, ceci n'est que la garde d'affichage.
 */
import * as React from "react";
import { X, Send, Users, ClipboardList, Download, Ban, RotateCcw } from "lucide-react";
import { userApi } from "@/services/index.js";
import * as api from "@/services/alerteEquipe.api.js";
import { downloadCsv } from "@/lib/csv-export.js";
import { useAuthStore } from "@/stores/authStore.js";
import useFocusTrap from "@/hooks/useFocusTrap.js";
import { useShortcuts } from "@/hooks/useShortcuts.js";
import { GLOBAL_SHORTCUTS } from "@/config/shortcuts.config.js";
import {
  LIBELLE_PALIER,
  EVENEMENT_OUVRIR_PANNEAU,
  RACCOURCI_PANNEAU,
  peutRediger,
  peutEnvoyerUrgent,
  peutVoirAccuses,
} from "./alerte-equipe.config.js";
import "./console.css";

const PALIERS = ["INFO", "WARNING", "CRITICAL"];
const ROLES = ["PROPRIETAIRE", "MANAGER", "CAISSIER"];
const LIBELLE_ROLE = {
  PROPRIETAIRE: "Titulaire",
  MANAGER: "Manager",
  CAISSIER: "Caissier",
};
const LIBELLE_ETAT = {
  ACCUSE: "Accusé",
  VU_NON_ACCUSE: "Vu, non accusé",
  NON_VU: "Non vu",
};

const horodatage = (v) => (v ? new Date(v).toLocaleString("fr-DZ") : "—");

/** Message d'erreur lisible depuis l'enveloppe d'erreur du serveur. */
function messageErreur(e, repli) {
  return e?.response?.data?.message ?? e?.message ?? repli;
}

// ═════════════════════════════════════════════════════════════════════════════
// Vue « Nouvelle alerte »
// ═════════════════════════════════════════════════════════════════════════════

function VueRedaction({ user, onEnvoyee }) {
  const [titre, setTitre] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [niveau, setNiveau] = React.useState("INFO");
  const [cible, setCible] = React.useState("TOUS");
  const [roles, setRoles] = React.useState([]);
  const [personnes, setPersonnes] = React.useState([]);
  const [equipe, setEquipe] = React.useState([]);
  const [apercu, setApercu] = React.useState(null);
  const [occupe, setOccupe] = React.useState(false);
  const [erreur, setErreur] = React.useState(null);

  const urgentPermis = peutEnvoyerUrgent(user);

  // L'annuaire n'est chargé QUE si on cible des personnes — un titulaire qui
  // n'envoie qu'à « toute l'équipe » ne déclenche aucune requête de plus.
  React.useEffect(() => {
    if (cible !== "PERSONNES" || equipe.length > 0) return;
    userApi
      .list({ limit: 200 })
      .then((r) => {
        const liste = Array.isArray(r?.data?.data)
          ? r.data.data
          : Array.isArray(r?.data)
            ? r.data
            : [];
        setEquipe(liste.filter((u) => ROLES.includes(u.role) && u.actif !== false));
      })
      .catch(() => setEquipe([]));
  }, [cible, equipe.length]);

  const basculer = (liste, set, valeur) =>
    set(liste.includes(valeur) ? liste.filter((v) => v !== valeur) : [...liste, valeur]);

  const corps = () => ({
    titre: titre.trim(),
    message: message.trim(),
    niveau,
    cible,
    roles_cibles: cible === "ROLES" ? roles : [],
    ...(cible === "PERSONNES" ? { user_ids: personnes } : {}),
  });

  /**
   * Aperçu = CRÉER le brouillon puis demander son décompte. Le brouillon n'est
   * pas un déchet : c'est l'objet qu'on enverra ensuite, et le cycle
   * brouillon → aperçu → envoi est celui de `broadcast.service.js` (L-5).
   */
  const verifier = async () => {
    setOccupe(true);
    setErreur(null);
    try {
      const brouillon = apercu?.alerte_id
        ? await api.modifier(apercu.alerte_id, corps())
        : await api.creer(corps());
      const compte = await api.apercu(brouillon.id);
      setApercu({ alerte_id: brouillon.id, ...compte });
    } catch (e) {
      setErreur(messageErreur(e, "Impossible de préparer l'alerte."));
    } finally {
      setOccupe(false);
    }
  };

  const envoyer = async () => {
    if (!apercu?.alerte_id) return;
    setOccupe(true);
    setErreur(null);
    try {
      const envoyee = await api.envoyer(apercu.alerte_id);
      setTitre("");
      setMessage("");
      setNiveau("INFO");
      setCible("TOUS");
      setRoles([]);
      setPersonnes([]);
      setApercu(null);
      onEnvoyee?.(envoyee);
    } catch (e) {
      setErreur(messageErreur(e, "L'envoi a échoué."));
    } finally {
      setOccupe(false);
    }
  };

  // Toute modification de la saisie périme l'aperçu : on ne veut pas envoyer
  // un décompte calculé sur une audience qui n'est plus celle affichée.
  const changer = (setteur) => (valeur) => {
    setApercu(null);
    setteur(valeur);
  };

  const complet = titre.trim().length >= 3 && message.trim().length >= 3;

  return (
    <div className="pk-ac-vue" data-testid="alerte-console-redaction">
      <label className="pk-ac-champ">
        <span className="pk-ac-label">Titre</span>
        <input
          type="text"
          className="pk-ac-input"
          data-testid="alerte-titre"
          value={titre}
          maxLength={80}
          onChange={(e) => changer(setTitre)(e.target.value)}
          placeholder="Ex. : Contrôle DPSP demain matin"
        />
      </label>

      <label className="pk-ac-champ">
        <span className="pk-ac-label">Message</span>
        <textarea
          className="pk-ac-textarea"
          data-testid="alerte-message"
          value={message}
          maxLength={500}
          rows={4}
          onChange={(e) => changer(setMessage)(e.target.value)}
          placeholder="Ce que l'équipe doit savoir, en une phrase claire."
        />
        <span className="pk-ac-compteur">{message.length} / 500</span>
      </label>

      <fieldset className="pk-ac-groupe">
        <legend className="pk-ac-label">Palier</legend>
        <div className="pk-ac-choix">
          {PALIERS.map((n) => {
            const interdit = n === "CRITICAL" && !urgentPermis;
            return (
              <button
                key={n}
                type="button"
                className="pk-ac-pastille"
                data-testid={`alerte-palier-${n}`}
                data-actif={niveau === n ? "true" : "false"}
                data-palier={n}
                disabled={interdit}
                title={
                  interdit
                    ? "Le palier URGENT bloque le comptoir : seul le titulaire peut l'émettre."
                    : undefined
                }
                onClick={() => changer(setNiveau)(n)}
              >
                {LIBELLE_PALIER[n]}
              </button>
            );
          })}
        </div>
        <p className="pk-ac-aide">
          {niveau === "CRITICAL"
            ? "Bandeau bloquant sur tous les écrans, avec accusé de réception obligatoire. Le comptoir est interrompu."
            : niveau === "WARNING"
              ? "Notification persistante : elle reste affichée jusqu'à ce que la personne la ferme."
              : "Bandeau discret en haut du contenu, refermable."}
        </p>
      </fieldset>

      <fieldset className="pk-ac-groupe">
        <legend className="pk-ac-label">Destinataires</legend>
        <div className="pk-ac-choix">
          {[
            ["TOUS", "Toute l'équipe"],
            ["ROLES", "Par rôle"],
            ["PERSONNES", "Personnes"],
          ].map(([v, l]) => (
            <button
              key={v}
              type="button"
              className="pk-ac-pastille"
              data-testid={`alerte-cible-${v}`}
              data-actif={cible === v ? "true" : "false"}
              onClick={() => changer(setCible)(v)}
            >
              {l}
            </button>
          ))}
        </div>

        {cible === "ROLES" && (
          <div className="pk-ac-choix pk-ac-choix-sous">
            {ROLES.map((r) => (
              <button
                key={r}
                type="button"
                className="pk-ac-pastille"
                data-testid={`alerte-role-${r}`}
                data-actif={roles.includes(r) ? "true" : "false"}
                onClick={() => {
                  setApercu(null);
                  basculer(roles, setRoles, r);
                }}
              >
                {LIBELLE_ROLE[r]}
              </button>
            ))}
          </div>
        )}

        {cible === "PERSONNES" && (
          <div className="pk-ac-liste-personnes">
            {equipe.length === 0 && <p className="pk-ac-aide">Chargement de l&apos;équipe…</p>}
            {equipe.map((u) => (
              <label key={u.id} className="pk-ac-personne">
                <input
                  type="checkbox"
                  checked={personnes.includes(u.id)}
                  onChange={() => {
                    setApercu(null);
                    basculer(personnes, setPersonnes, u.id);
                  }}
                />
                <span>
                  {u.prenom} {u.nom}
                </span>
                <span className="pk-ac-role">{LIBELLE_ROLE[u.role] ?? u.role}</span>
              </label>
            ))}
          </div>
        )}
      </fieldset>

      {erreur && (
        <p className="pk-ac-erreur" role="alert" data-testid="alerte-console-erreur">
          {erreur}
        </p>
      )}

      {apercu && (
        <div className="pk-ac-apercu" data-testid="alerte-apercu">
          <Users size={14} aria-hidden="true" />
          <span>
            {/* `nb_destinataires` — le nom que rend `apercu()`. Ne pas le
                confondre avec le `total` du tableau d'accusés, qui compte des
                lignes DÉJÀ matérialisées. */}
            <strong data-testid="alerte-apercu-total">{apercu.nb_destinataires ?? 0}</strong>{" "}
            personne
            {(apercu.nb_destinataires ?? 0) > 1 ? "s" : ""} recevr
            {(apercu.nb_destinataires ?? 0) > 1 ? "ont" : "a"} cette alerte
          </span>
          {Array.isArray(apercu.apercu) && apercu.apercu.length > 0 && (
            <span className="pk-ac-apercu-noms">
              {apercu.apercu
                .map((p) => `${p.prenom ?? ""} ${p.nom ?? ""}`.trim())
                .filter(Boolean)
                .join(", ")}
            </span>
          )}
        </div>
      )}

      <div className="pk-ac-actions">
        {!apercu ? (
          <button
            type="button"
            className="pk-ac-bouton"
            data-testid="alerte-verifier"
            disabled={!complet || occupe}
            onClick={verifier}
          >
            <Users size={15} aria-hidden="true" />
            {occupe ? "Préparation…" : "Vérifier les destinataires"}
          </button>
        ) : (
          <button
            type="button"
            className="pk-ac-bouton pk-ac-bouton-primaire"
            data-testid="alerte-envoyer"
            data-palier={niveau}
            disabled={occupe || (apercu.nb_destinataires ?? 0) === 0}
            onClick={envoyer}
          >
            <Send size={15} aria-hidden="true" />
            {occupe ? "Envoi…" : `Envoyer — ${LIBELLE_PALIER[niveau]}`}
          </button>
        )}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Vue « Historique » + le tableau d'accusés (§3.6)
// ═════════════════════════════════════════════════════════════════════════════

function VueHistorique({ user, rafraichissement }) {
  const [alertes, setAlertes] = React.useState([]);
  const [ouverte, setOuverte] = React.useState(null);
  const [roster, setRoster] = React.useState(null);
  const [erreur, setErreur] = React.useState(null);
  const autorise = peutVoirAccuses(user);

  const charger = React.useCallback(() => {
    api
      .lister({ limit: 30 })
      .then((r) => setAlertes(r.data ?? []))
      .catch((e) => setErreur(messageErreur(e, "Historique indisponible.")));
  }, []);

  React.useEffect(() => {
    charger();
  }, [charger, rafraichissement]);

  const ouvrir = async (id) => {
    if (ouverte === id) {
      setOuverte(null);
      setRoster(null);
      return;
    }
    setOuverte(id);
    setRoster(null);
    if (!autorise) return;
    try {
      setRoster(await api.accuses(id));
    } catch (e) {
      setErreur(messageErreur(e, "Tableau d'accusés indisponible."));
    }
  };

  const annuler = async (id) => {
    try {
      await api.annuler(id);
      charger();
      if (ouverte === id) setRoster(await api.accuses(id));
    } catch (e) {
      setErreur(messageErreur(e, "L'annulation a échoué."));
    }
  };

  const reemettre = async (id) => {
    try {
      await api.reemettre(id);
      charger();
    } catch (e) {
      setErreur(messageErreur(e, "La réémission a échoué."));
    }
  };

  return (
    <div className="pk-ac-vue" data-testid="alerte-console-historique">
      {erreur && (
        <p className="pk-ac-erreur" role="alert">
          {erreur}
        </p>
      )}
      {alertes.length === 0 && <p className="pk-ac-aide">Aucune alerte pour le moment.</p>}

      {alertes.map((a) => (
        <div key={a.id} className="pk-ac-ligne" data-testid="alerte-historique-ligne">
          <button
            type="button"
            className="pk-ac-ligne-tete"
            onClick={() => ouvrir(a.id)}
            aria-expanded={ouverte === a.id}
            data-testid="alerte-historique-ouvrir"
          >
            <span className="pk-ac-badge" data-palier={a.niveau}>
              {LIBELLE_PALIER[a.niveau] ?? a.niveau}
            </span>
            <span className="pk-ac-ligne-titre">{a.titre}</span>
            <span className="pk-ac-badge-statut" data-statut={a.statut}>
              {a.statut}
            </span>
            <span className="pk-ac-ligne-date">{horodatage(a.envoye_at ?? a.created_at)}</span>
          </button>

          {ouverte === a.id && (
            <div className="pk-ac-ligne-detail">
              <p className="pk-ac-ligne-message">{a.message}</p>

              {!autorise && (
                <p className="pk-ac-aide">
                  Le tableau des accusés n&apos;est visible que par le titulaire et le
                  manager de l&apos;officine.
                </p>
              )}

              {autorise && roster && (
                <>
                  <div className="pk-ac-roster-entete" data-testid="alerte-roster-entete">
                    <ClipboardList size={14} aria-hidden="true" />
                    <strong data-testid="alerte-roster-compte">
                      {roster.accuses} / {roster.total}
                    </strong>
                    <span>ont accusé réception</span>
                    {/* PAS un <a href> : l'export est authentifié par en-tête
                        `Authorization`, qu'un lien de navigation ne porte pas —
                        il rendrait un 401. On passe par le helper d'export
                        maison (`lib/csv-export.js`), qui traverse le client
                        axios partagé et déclenche le téléchargement. */}
                    <button
                      type="button"
                      className="pk-ac-lien"
                      data-testid="alerte-roster-csv"
                      onClick={() =>
                        downloadCsv(api.accusesCsvUrl(a.id), {}, `accuses-alerte`).catch(
                          (e) => setErreur(messageErreur(e, "Export impossible.")),
                        )
                      }
                    >
                      <Download size={13} aria-hidden="true" /> CSV
                    </button>
                  </div>

                  <ul className="pk-ac-roster">
                    {roster.destinataires.map((d) => (
                      <li
                        key={d.destinataire_id}
                        className="pk-ac-roster-ligne"
                        data-etat={d.etat}
                        data-testid="alerte-roster-ligne"
                      >
                        <span className="pk-ac-roster-nom">
                          {d.user?.prenom} {d.user?.nom}
                        </span>
                        <span className="pk-ac-role">
                          {LIBELLE_ROLE[d.user?.role] ?? d.user?.role}
                        </span>
                        <span className="pk-ac-roster-etat">{LIBELLE_ETAT[d.etat]}</span>
                        <span className="pk-ac-roster-quand">
                          {d.etat === "ACCUSE"
                            ? horodatage(d.acked_at)
                            : d.etat === "VU_NON_ACCUSE"
                              ? horodatage(d.seen_at)
                              : "—"}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              <div className="pk-ac-actions pk-ac-actions-ligne">
                {a.statut === "ENVOYEE" && (
                  <button
                    type="button"
                    className="pk-ac-bouton pk-ac-bouton-discret"
                    data-testid="alerte-annuler"
                    onClick={() => annuler(a.id)}
                    title="Retire l'alerte de tous les écrans au prochain sondage."
                  >
                    <Ban size={14} aria-hidden="true" /> Annuler
                  </button>
                )}
                <button
                  type="button"
                  className="pk-ac-bouton pk-ac-bouton-discret"
                  data-testid="alerte-reemettre"
                  onClick={() => reemettre(a.id)}
                  title="Crée un nouveau brouillon qui remplace celui-ci (on ne modifie jamais un envoi)."
                >
                  <RotateCcw size={14} aria-hidden="true" /> Réémettre
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Le tiroir
// ═════════════════════════════════════════════════════════════════════════════

/** La touche de la console dans sa propre portée (celle du registre global). */
const CONSOLE_KEYS = Object.freeze(GLOBAL_SHORTCUTS.filter((b) => b.id === "ALERTES_EQUIPE"));

export default function AlerteConsole() {
  const user = useAuthStore((s) => s.user);
  const [ouvert, setOuvert] = React.useState(false);
  const [vue, setVue] = React.useState("redaction");
  const [rafraichissement, setRafraichissement] = React.useState(0);
  const refPiege = useFocusTrap(ouvert, () => setOuvert(false));

  const autorise = peutRediger(user);

  // Ouverture : le raccourci clavier (RACCOURCI_PANNEAU, désormais une entrée
  // du registre GLOBAL_SHORTCUTS — WP-17 : un seul répartiteur, la touche
  // figure dans chaque feuille F1, modificateurs exacts, AltGr ne l'ouvre
  // jamais), ou l'événement fenêtre pour qu'un bouton d'écran puisse l'ouvrir
  // sans que ce composant soit son parent.
  useShortcuts(
    "GLOBAL",
    { ALERTES_EQUIPE: () => setOuvert((v) => !v) },
    { enabled: autorise },
  );
  // Console ouverte (dialogue modal) : la même touche la referme — les
  // touches globales sont suspendues sous un modal, sauf celles du dialogue.
  useShortcuts(
    "ALERTES_CONSOLE",
    { ALERTES_EQUIPE: () => setOuvert(false) },
    { kind: "overlay", entries: CONSOLE_KEYS, enabled: autorise && ouvert, title: "Alertes équipe" },
  );
  React.useEffect(() => {
    if (!autorise) return undefined;
    const onEvenement = () => setOuvert(true);
    window.addEventListener(EVENEMENT_OUVRIR_PANNEAU, onEvenement);
    return () => window.removeEventListener(EVENEMENT_OUVRIR_PANNEAU, onEvenement);
  }, [autorise]);

  if (!autorise || !ouvert) return null;

  return (
    <div className="pk-ac-voile" data-testid="alerte-console">
      {/* Ce voile-ci FERME au clic : la console est un outil de rédaction
          ordinaire, pas la surcouche bloquante — ne pas confondre les deux. */}
      <button
        type="button"
        className="pk-ac-voile-fond"
        aria-label="Fermer la console d'alertes"
        onClick={() => setOuvert(false)}
      />
      <div
        ref={refPiege}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pk-ac-titre"
        className="pk-ac-tiroir"
      >
        <header className="pk-ac-entete">
          <h2 id="pk-ac-titre" className="pk-ac-entete-titre">
            Alertes équipe
          </h2>
          <button
            type="button"
            className="pk-ac-fermer"
            aria-label="Fermer"
            data-testid="alerte-console-fermer"
            onClick={() => setOuvert(false)}
          >
            <X size={16} aria-hidden="true" />
          </button>
        </header>

        <div className="pk-ac-onglets" role="tablist">
          {[
            ["redaction", "Nouvelle alerte"],
            ["historique", "Historique"],
          ].map(([v, l]) => (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={vue === v}
              className="pk-ac-onglet"
              data-actif={vue === v ? "true" : "false"}
              data-testid={`alerte-onglet-${v}`}
              onClick={() => setVue(v)}
            >
              {l}
            </button>
          ))}
        </div>

        <div className="pk-ac-contenu">
          {vue === "redaction" ? (
            <VueRedaction
              user={user}
              onEnvoyee={() => {
                setRafraichissement((n) => n + 1);
                setVue("historique");
              }}
            />
          ) : (
            <VueHistorique user={user} rafraichissement={rafraichissement} />
          )}
        </div>

        <footer className="pk-ac-pied">
          {/* §5.7 Loi 18-07 — la mention d'information, à l'endroit où le
              titulaire crée le traitement, pas enfouie dans une page d'aide. */}
          Les accusés de réception sont horodatés et visibles par le titulaire de
          l&apos;officine. Raccourci : {RACCOURCI_PANNEAU.libelle}.
        </footer>
      </div>
    </div>
  );
}
